import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Link } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { RBACProvider } from './contexts/RBACContext';
import { LocationProvider } from './contexts/LocationContext';
import { InventoryProvider } from './contexts/InventoryContext';
import { CRMProvider } from './contexts/CRMContext';
import { ReminderProvider } from './contexts/ReminderContext';

// Auth Components
import Login from './components/Auth/Login';
import Register from './components/Auth/Register';
import ForgotPassword from './components/Auth/ForgotPassword';
import ResetPassword from './components/Auth/ResetPassword';
import VerifyEmail from './components/Auth/VerifyEmail';
import Dashboard from './components/Dashboard/Dashboard';
import ProtectedRoute from './components/Common/ProtectedRoute';
import LoadingSpinner from './components/Common/LoadingSpinner';
import MainLayout from './components/Layout/MainLayout';
import MemberLogin from './components/Membership/Auth/MemberLogin';

// Public Application Forms
import PublicApplicationForm from './components/CRM/Applications/PublicApplicationForm';
import PublicApplicationByNumber from './components/CRM/Applications/PublicApplicationByNumber';
import PublicCenterSelfRegistration from './components/CRM/Centers/PublicCenterSelfRegistration';
import PublicCenterSettlementPay from './components/CRM/Centers/PublicCenterSettlementPay';
import PublicDocumentSubmit from './components/CRM/Applications/PublicDocumentSubmit';
import PublicScholarshipProofUpload from './components/CRM/Applications/PublicScholarshipProofUpload';
import PublicEnrollmentDocumentSubmit from './components/CRM/Education/Enrollments/PublicEnrollmentDocumentSubmit';
import PublicPaymentSubmit from './components/CRM/Applications/PublicPaymentSubmit';
import PublicPMApplicationForm from './components/Membership/PracticingMarketer/PublicPMApplicationForm';
import PublicProcessingFeePayment from './components/CRM/Applications/PublicProcessingFeePayment';
import PublicWorkshopPaymentSubmit from './components/CRM/TW/Public/PublicWorkshopPaymentSubmit';
import PublicTWPayment from './components/CRM/TW/Public/PublicTWPayment';
import PublicTWPaymentResult from './components/CRM/TW/Public/PublicTWPaymentResult';
import PublicEnrollmentPayment from './components/CRM/Education/Enrollments/PublicEnrollmentPayment';
import PublicGraduationDepositPayment from './components/CRM/Education/Graduations/PublicGraduationDepositPayment';
import GraduationAttendanceCheckin from './components/CRM/Education/Graduations/GraduationAttendanceCheckin';
import StaffGraduationDepositReview from './components/CRM/Education/Graduations/StaffGraduationDepositReview';
import PublicEventApplicationForm from './components/Events/ApplicationForms/PublicApplicationForm';
import PublicEventFeedback from './components/Events/Feedback/PublicEventFeedback';
import PublicEventResults from './components/Events/Results/PublicEventResults';
import EventExternalPortalApplyEntry from './components/Events/ApplicationForms/EventExternalPortalApplyEntry';
import PublicProformaPaymentPage from './components/Events/ExternalCompanyPortal/PublicProformaPaymentPage';
import PublicSponsorshipPaymentPage from './components/Events/Sponsors/PublicSponsorshipPaymentPage';
import PublicEventTicketPurchasePage from './components/Events/Ticketing/PublicEventTicketPurchasePage';
import PublicEventTicketPaymentPage from './components/Events/Ticketing/PublicEventTicketPaymentPage';
import PublicEventTicketCheckInManifestPage from './components/Events/Ticketing/PublicEventTicketCheckInManifestPage';
import PublicEventTicketDoorCheckInPage from './components/Events/Ticketing/PublicEventTicketDoorCheckInPage';
import PublicEventTicketTransferPage from './components/Events/Ticketing/PublicEventTicketTransferPage';
import PublicEventTicketTransferRequestPage from './components/Events/Ticketing/PublicEventTicketTransferRequestPage';
import PublicSponsorContractPaymentPage from './components/Events/Sponsors/PublicSponsorContractPaymentPage';
import PublicDirectPaymentPage from './components/Events/EventDetail/PublicDirectPaymentPage';
import PublicMembershipPaymentSubmit from './components/Membership/Payments/PublicMembershipPaymentSubmit';
import PublicReceiptReupload from './components/Membership/Payments/PublicReceiptReupload';
import PublicMembershipPaymentCallback from './components/Membership/Payments/PublicMembershipPaymentCallback';
import PublicMembershipDocumentSubmit from './components/Membership/Applications/PublicMembershipDocumentSubmit';
import PublicMembershipApplicationForm from './components/Membership/Applications/PublicMembershipApplicationForm';
import PublicTicketSale from './components/Membership/Ticketing/public/PublicTicketSale';
import PublicTicketOrder from './components/Membership/Ticketing/public/PublicTicketOrder';

// RBAC Components
import RoleManagement from './components/RBAC/Roles/RoleManagement';
import PermissionManagement from './components/RBAC/Permissions/PermissionManagement';
import UserManagement from './components/RBAC/Users/UserManagement';

// Inventory Components
import InventoryDashboard from './components/Inventory/Dashboard/InventoryDashboard';
import CategoryManagement from './components/Inventory/Categories/CategoryManagement';
import ItemManagement from './components/Inventory/Items/ItemManagement';
import ServiceManagement from './components/Inventory/Services/ServiceManagement';
import StockManagement from './components/Inventory/Stock/StockManagement';
import StockOverview from './components/Inventory/Stock/StockOverview';
import SupplierManagement from './components/Inventory/Suppliers/SupplierManagement';
import BincardReport from './components/Inventory/Reports/BincardReport';
import StockLevelsReport from './components/Inventory/Reports/StockLevelsReport';
import StockMovementsReport from './components/Inventory/Reports/StockMovementsReport';
import StockAnalyticsReport from './components/Inventory/Reports/StockAnalyticsReport';
import StockAgeAnalysisReport from './components/Inventory/Reports/StockAgeAnalysisReport';
import StockValueReport from './components/Inventory/Reports/StockValueReport';
import ReorderReport from './components/Inventory/Reports/ReorderReport';
import StockMovementSummaryReport from './components/Inventory/Reports/StockMovementSummaryReport';
import ConsumptionReport from './components/Inventory/Reports/ConsumptionReport';
import PriceListReport from './components/Inventory/Reports/PriceListReport';
import StockOperationsReport from './components/Inventory/Reports/StockOperationsReport';
import PurchaseOrderManagement from './components/Inventory/PurchaseOrders/PurchaseOrderManagement';
import GRNManagement from './components/Inventory/GRN/GRNManagement';
import SupplierPaymentManagement from './components/Inventory/SupplierPayments/SupplierPaymentManagement';
import SupplierPaymentView from './components/Inventory/SupplierPayments/SupplierPaymentView';
import SupplierReturnNoteManagement from './components/Inventory/SupplierReturnNotes/SupplierReturnNoteManagement';
import DebitNoteManagement from './components/Inventory/DebitNotes/DebitNoteManagement';

// Purchase Request Components
import PurchaseRequestList from './components/Inventory/PurchaseRequests/PurchaseRequestList';
import QuotationList from './components/Inventory/Quotations/QuotationList';
import PurchaseRequestForm from './components/Inventory/PurchaseRequests/PurchaseRequestForm';

// Goods Transfer Request Components
import GTRList from './components/Inventory/GoodsTransferRequests/GTRList';
import GTRForm from './components/Inventory/GoodsTransferRequests/GTRForm';
import GTRDetails from './components/Inventory/GoodsTransferRequests/GTRDetails';

// Goods Issue Note Components
import GoodsIssueNoteList from './components/Inventory/GoodsIssueNotes/GoodsIssueNoteList';
import MaterialRequestNoteList from './components/Inventory/MaterialRequestNotes/MaterialRequestNoteList';
import InventoryConsumptionList from './components/Inventory/Consumption/InventoryConsumptionList';

// Transfer In Components
import TransferInList from './components/Inventory/TransferIn/TransferInList';

import RouteList from './components/Inventory/Routes/RouteList';
import ReturnTypesList from './components/Inventory/ReturnTypes/ReturnTypesList.jsx';
import SalesRefList from './components/Inventory/SalesRefs/SalesRefList';
import PriceLevels from './components/Inventory/Pricing/PriceLevels';
import TaxList from './components/Inventory/Taxes/TaxList';
import SalesChannelList from './components/Inventory/SalesChannels/SalesChannelList';

// Inventory Settings
import POApprovalLevels from './components/Inventory/Settings/POApprovalLevels';

// Location Management Components
import LocationsList from './components/Admin/Locations/LocationsList';
import LocationDetails from './components/Admin/Locations/LocationDetails';
import SignUpSettings from './components/Admin/SignUpSettings';
import UserPermissionsReport from './components/Admin/UserPermissionsReport';
import AccessHistoryViewer from './components/Admin/AccessHistoryViewer';
import HRMAuditLogsViewer from './components/HRM/Reports/AuditLogsViewer';
import CRMAuditLogsViewer from './components/CRM/Reports/AuditLogsViewer';
import MembershipAuditLogsViewer from './components/Membership/Reports/AuditLogsViewer';
import FinanceAuditLogsViewer from './components/Finance/Reports/AuditLogsViewer';
import EventAuditLogsViewer from './components/Events/Reports/AuditLogsViewer';
import AdminAuditLogsViewer from './components/Admin/Reports/AuditLogsViewer';
// User locations management removed - now handled in User Management page
import UserProfile from './components/User/UserProfile';

// Lazy-loaded heavy modules
const FinanceModule = lazy(() => import('./components/Finance/FinanceModule'));
const CRMModule = lazy(() => import('./components/CRM/CRMModule'));
const HRMModule = lazy(() => import('./components/HRM/HRMModule'));
const MembershipModule = lazy(() => import('./components/Membership/MembershipModule'));
const EventsModule = lazy(() => import('./components/Events/EventsModule'));

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// App Layout Component
const AppLayout = ({ children }) => {
  return (
    <div className="min-h-screen bg-gray-50">
      {children}
      <Toaster
        position="top-right"
        // above every modal / dropdown layer (some use z-index 99999)
        containerStyle={{ zIndex: 1000000 }}
        toastOptions={{
          duration: 4000,
          style: {
            background: '#363636',
            color: '#fff',
          },
          success: {
            iconTheme: {
              primary: '#4ade80',
              secondary: '#fff',
            },
          },
          error: {
            iconTheme: {
              primary: '#ef4444',
              secondary: '#fff',
            },
          },
        }}
      />
    </div>
  );
};

// Main App Component
const AppContent = () => {
  const { isLoading } = useAuth();

  if (isLoading) {
    return (
      <AppLayout>
        <LoadingSpinner />
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <Routes>
        {/* Public Routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/verify-email" element={<VerifyEmail />} />

        {/* Membership Portal Routes */}
        <Route path="/membership/login" element={<MemberLogin />} />

        {/* Public Application Forms */}
        <Route path="/apply/:token" element={<PublicApplicationForm />} />
        <Route path="/apply/public/:id" element={<PublicApplicationByNumber />} />
        <Route path="/centers/register/:locationId" element={<PublicCenterSelfRegistration />} />
        <Route path="/centers/settlement-pay/:token" element={<PublicCenterSettlementPay />} />
        <Route path="/submit-documents/:token" element={<PublicDocumentSubmit />} />
        <Route path="/scholarship-proof/:token" element={<PublicScholarshipProofUpload />} />
        <Route path="/enrollment-submit-documents/:token" element={<PublicEnrollmentDocumentSubmit />} />
        <Route path="/processing-fee/:token" element={<PublicProcessingFeePayment />} />
        <Route path="/public/workshop-payment/:token" element={<PublicWorkshopPaymentSubmit />} />
        <Route path="/public/tw-payment/success" element={<PublicTWPaymentResult type="success" />} />
        <Route path="/public/tw-payment/error" element={<PublicTWPaymentResult type="error" />} />
        <Route path="/public/tw-payment/:token" element={<PublicTWPayment />} />
        <Route path="/pay/enrollment/:token" element={<PublicEnrollmentPayment />} />
        <Route path="/pay/graduation-deposit/:token" element={<PublicGraduationDepositPayment />} />

        {/* Protected Graduation Staff Routes */}
        <Route
          path="/crm/graduations/:id/attendance-checkin"
          element={
            <ProtectedRoute>
              <MainLayout>
                <GraduationAttendanceCheckin />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/crm/graduations/:id/deposit-review"
          element={
            <ProtectedRoute>
              <MainLayout>
                <StaffGraduationDepositReview />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* Public Events payment + feedback pages. Application forms are NOT public:
            external companies must register and log in to the portal to apply. */}
        <Route path="/public/event-proforma-payment/:token" element={<PublicProformaPaymentPage />} />
        <Route path="/public/event-sponsorship-payment/:token" element={<PublicSponsorshipPaymentPage />} />
        <Route path="/public/sponsor-contract-payment/:token" element={<PublicSponsorContractPaymentPage />} />
        <Route path="/public/direct-payment/:token" element={<PublicDirectPaymentPage />} />
        <Route path="/public/events/:eventId/tickets" element={<PublicEventTicketPurchasePage />} />
        <Route path="/public/events/:eventId/tickets/transfer" element={<PublicEventTicketTransferRequestPage />} />
        <Route path="/public/event-ticket-payment/:token" element={<PublicEventTicketPaymentPage />} />
        <Route path="/public/events/ticket-door-checkin/:token" element={<PublicEventTicketDoorCheckInPage />} />
        <Route path="/public/events/ticket-checkin-manifest/:token" element={<PublicEventTicketCheckInManifestPage />} />
        <Route path="/public/event-ticket-transfer/:token" element={<PublicEventTicketTransferPage />} />
        <Route path="/public/event-feedback/:eventId" element={<PublicEventFeedback />} />
        <Route path="/public/event-results/:eventId" element={<PublicEventResults />} />

        {/* Public payment/application routes (No layout) */}
        <Route path="/payment/submit/:token" element={<PublicPaymentSubmit />} />
        <Route path="/membership/payment/reupload/:token" element={<PublicReceiptReupload />} />
        <Route path="/membership/payment/:token" element={<PublicMembershipPaymentSubmit />} />
        <Route path="/membership/payment/:token/callback" element={<PublicMembershipPaymentCallback />} />
        <Route path="/membership/pm-applications/public/:token" element={<PublicPMApplicationForm />} />
        <Route path="/membership/submit-documents/:token" element={<PublicMembershipDocumentSubmit />} />
        <Route path="/membership/apply/:token" element={<PublicMembershipApplicationForm />} />
        {/* Public event ticket sales (membership events) */}
        <Route path="/membership/tickets/:token" element={<PublicTicketSale />} />
        <Route path="/membership/tickets/:token/order/:orderToken" element={<PublicTicketOrder />} />

        {/* Coordinator portal apply URL: anonymous → public form; logged-in → full shell + portal mode */}
        <Route path="/events/external-portal/apply/:token" element={<EventExternalPortalApplyEntry />} />

        {/* Protected Preview (shows the public UI using the public token) */}
        <Route
          path="/events/application-forms/preview/:token"
          element={
            <ProtectedRoute>
              <PublicEventApplicationForm />
            </ProtectedRoute>
          }
        />

        {/* Protected Routes */}
        <Route path="/dashboard" element={
          <ProtectedRoute>
            <MainLayout>
              <Dashboard />
            </MainLayout>
          </ProtectedRoute>
        } />

        {/* Admin Routes */}
        <Route path="/admin/users" element={
          <ProtectedRoute>
            <MainLayout>
              <UserManagement />
            </MainLayout>
          </ProtectedRoute>
        } />

        <Route path="/admin/reports" element={
          <ProtectedRoute>
            <MainLayout>
              <UserPermissionsReport />
            </MainLayout>
          </ProtectedRoute>
        } />

        <Route path="/admin/reports/access-history" element={
          <ProtectedRoute>
            <MainLayout>
              <AccessHistoryViewer />
            </MainLayout>
          </ProtectedRoute>
        } />

        <Route path="/admin/reports/audit-logs/hrm" element={
          <ProtectedRoute requiredPermission="ADMIN:REPORTS_AUDIT_LOGS_HRM:READ" disallowedRoles={['ADMIN']}>
            <MainLayout>
              <HRMAuditLogsViewer />
            </MainLayout>
          </ProtectedRoute>
        } />

        <Route path="/admin/reports/audit-logs/crm" element={
          <ProtectedRoute requiredPermission="ADMIN:REPORTS_AUDIT_LOGS_CRM:READ">
            <MainLayout>
              <CRMAuditLogsViewer />
            </MainLayout>
          </ProtectedRoute>
        } />

        <Route path="/admin/reports/audit-logs/membership" element={
          <ProtectedRoute requiredPermission="ADMIN:REPORTS_AUDIT_LOGS_MEM:READ">
            <MainLayout>
              <MembershipAuditLogsViewer />
            </MainLayout>
          </ProtectedRoute>
        } />

        <Route path="/admin/reports/audit-logs/finance" element={
          <ProtectedRoute requiredPermission="ADMIN:REPORTS_AUDIT_LOGS_FIN:READ">
            <MainLayout>
              <FinanceAuditLogsViewer />
            </MainLayout>
          </ProtectedRoute>
        } />

        <Route path="/admin/reports/audit-logs/event" element={
          <ProtectedRoute requiredPermission="ADMIN:REPORTS_AUDIT_LOGS_EVENT:READ">
            <MainLayout>
              <EventAuditLogsViewer />
            </MainLayout>
          </ProtectedRoute>
        } />

        <Route path="/admin/reports/audit-logs/administration" element={
          <ProtectedRoute requiredPermission="ADMIN:REPORTS_AUDIT_LOGS_ADMIN:READ">
            <MainLayout>
              <AdminAuditLogsViewer />
            </MainLayout>
          </ProtectedRoute>
        } />

        <Route path="/admin/roles" element={
          <ProtectedRoute>
            <MainLayout>
              <RoleManagement />
            </MainLayout>
          </ProtectedRoute>
        } />

        <Route path="/admin/permissions" element={
          <ProtectedRoute>
            <MainLayout>
              <PermissionManagement />
            </MainLayout>
          </ProtectedRoute>
        } />

        {/* Location Management Routes */}
        <Route path="/admin/locations" element={
          <ProtectedRoute>
            <MainLayout>
              <LocationsList />
            </MainLayout>
          </ProtectedRoute>
        } />

        <Route path="/admin/locations/:id" element={
          <ProtectedRoute>
            <MainLayout>
              <LocationDetails />
            </MainLayout>
          </ProtectedRoute>
        } />

        <Route path="/admin/signup-settings" element={
          <ProtectedRoute>
            <MainLayout>
              <SignUpSettings />
            </MainLayout>
          </ProtectedRoute>
        } />

        {/* User Profile Routes */}
        <Route path="/profile" element={
          <ProtectedRoute>
            <MainLayout>
              <UserProfile />
            </MainLayout>
          </ProtectedRoute>
        } />

        {/* Finance Module Routes */}
        <Route path="/finance/*" element={
          <ProtectedRoute>
            <MainLayout>
              <Suspense fallback={<LoadingSpinner />}>
                <FinanceModule />
              </Suspense>
            </MainLayout>
          </ProtectedRoute>
        } />

        {/* CRM Module Routes */}
        <Route path="/crm/*" element={
          <ProtectedRoute>
            <MainLayout>
              <Suspense fallback={<LoadingSpinner />}>
                <CRMModule />
              </Suspense>
            </MainLayout>
          </ProtectedRoute>
        } />

        {/* HRM Module Routes */}
        <Route path="/hrm/*" element={
          <ProtectedRoute disallowedRoles={['ADMIN']}>
            <MainLayout>
              <Suspense fallback={<LoadingSpinner />}>
                <HRMModule />
              </Suspense>
            </MainLayout>
          </ProtectedRoute>
        } />

        {/* Membership Module Routes */}
        <Route path="/membership/*" element={
          <ProtectedRoute>
            <MainLayout>
              <Suspense fallback={<LoadingSpinner />}>
                <MembershipModule />
              </Suspense>
            </MainLayout>
          </ProtectedRoute>
        } />

        {/* Events Module Routes */}
        <Route path="/events/*" element={
          <ProtectedRoute>
            <MainLayout>
              <Suspense fallback={<LoadingSpinner />}>
                <EventsModule />
              </Suspense>
            </MainLayout>
          </ProtectedRoute>
        } />

        {/* Inventory Routes */}
        <Route path="/inventory/dashboard" element={
          <ProtectedRoute requiredPermission="INVENTORY:DASHBOARD:READ">
            <MainLayout>
              <InventoryDashboard />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/inventory/categories" element={
          <ProtectedRoute requiredPermission="INVENTORY:CATEGORIES:READ">
            <MainLayout>
              <CategoryManagement />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/inventory/purchase-orders" element={
          <ProtectedRoute>
            <MainLayout>
              <PurchaseOrderManagement />
            </MainLayout>
          </ProtectedRoute>
        } />

        {/* Supplier Quotations (per approved purchase request) */}
        <Route path="/inventory/supplier-quotations" element={
          <ProtectedRoute requiredPermission="INVENTORY:QUOTATIONS:READ">
            <MainLayout>
              <QuotationList />
            </MainLayout>
          </ProtectedRoute>
        } />

        {/* Purchase Request Routes */}
        <Route path="/inventory/purchase-requests" element={
          <ProtectedRoute requiredPermission="INVENTORY:PURCHASE_REQUESTS:READ">
            <MainLayout>
              <PurchaseRequestList />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/inventory/purchase-requests/new" element={
          <ProtectedRoute requiredPermission="INVENTORY:PURCHASE_REQUESTS:CREATE">
            <MainLayout>
              <PurchaseRequestForm />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/inventory/purchase-requests/edit/:id" element={
          <ProtectedRoute requiredPermission="INVENTORY:PURCHASE_REQUESTS:UPDATE">
            <MainLayout>
              <PurchaseRequestForm />
            </MainLayout>
          </ProtectedRoute>
        } />

        {/* Goods Transfer Request Routes */}
        <Route path="/inventory/goods-transfer-requests" element={
          <ProtectedRoute requiredPermission="INVENTORY:STOCK_TRANSFERS:READ">
            <MainLayout>
              <GTRList />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/inventory/goods-transfer-requests/new" element={
          <ProtectedRoute requiredPermission="INVENTORY:STOCK_TRANSFERS:CREATE">
            <MainLayout>
              <GTRForm />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/inventory/goods-transfer-requests/edit/:id" element={
          <ProtectedRoute requiredPermission="INVENTORY:STOCK_TRANSFERS:UPDATE">
            <MainLayout>
              <GTRForm />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/inventory/goods-transfer-requests/view/:id" element={
          <ProtectedRoute requiredPermission="INVENTORY:STOCK_TRANSFERS:READ">
            <MainLayout>
              <GTRDetails />
            </MainLayout>
          </ProtectedRoute>
        } />

        {/* Goods Issue Note Routes */}
        <Route path="/inventory/goods-issue-notes" element={
          <ProtectedRoute requiredPermission="INVENTORY:GOODS_ISSUE_NOTES:READ">
            <MainLayout>
              <GoodsIssueNoteList />
            </MainLayout>
          </ProtectedRoute>
        } />

        {/* Material Request Note Routes */}
        <Route path="/inventory/material-request-notes" element={
          <ProtectedRoute requiredPermission="INVENTORY:MATERIAL_REQUEST_NOTES:READ">
            <MainLayout>
              <MaterialRequestNoteList />
            </MainLayout>
          </ProtectedRoute>
        } />

        {/* Inventory Consumption Routes */}
        <Route path="/inventory/inventory-consumption" element={
          <ProtectedRoute requiredPermission="INVENTORY:INVENTORY_CONSUMPTION:READ">
            <MainLayout>
              <InventoryConsumptionList />
            </MainLayout>
          </ProtectedRoute>
        } />

        {/* Transfer In Routes */}
        <Route path="/inventory/transfer-in" element={
          <ProtectedRoute requiredPermission="INVENTORY:TRANSFER_IN:READ">
            <MainLayout>
              <TransferInList />
            </MainLayout>
          </ProtectedRoute>
        } />

        <Route path="/inventory/grn" element={
          <ProtectedRoute>
            <MainLayout>
              <GRNManagement />
            </MainLayout>
          </ProtectedRoute>
        } />

        {/* Supplier Payment Routes */}
        <Route path="/inventory/supplier-payments" element={
          <ProtectedRoute>
            <MainLayout>
              <SupplierPaymentManagement />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/inventory/supplier-payments/view/:id" element={
          <ProtectedRoute>
            <MainLayout>
              <SupplierPaymentView />
            </MainLayout>
          </ProtectedRoute>
        } />

        {/* Supplier Return Notes Routes */}
        <Route path="/inventory/supplier-return-notes" element={
          <ProtectedRoute>
            <MainLayout>
              <SupplierReturnNoteManagement />
            </MainLayout>
          </ProtectedRoute>
        } />

        {/* Debit Notes Routes */}
        <Route path="/inventory/debit-notes" element={
          <ProtectedRoute>
            <MainLayout>
              <DebitNoteManagement />
            </MainLayout>
          </ProtectedRoute>
        } />

        <Route path="/inventory/services" element={
          <ProtectedRoute requiredPermission="INVENTORY:SERVICES:READ">
            <MainLayout>
              <ServiceManagement />
            </MainLayout>
          </ProtectedRoute>
        } />

        <Route path="/inventory/items" element={
          <ProtectedRoute>
            <MainLayout>
              <ItemManagement />
            </MainLayout>
          </ProtectedRoute>
        } />

        <Route path="/inventory/stock" element={
          <ProtectedRoute>
            <MainLayout>
              <StockManagement />
            </MainLayout>
          </ProtectedRoute>
        } />

        <Route path="/inventory/stock-overview" element={
          <ProtectedRoute>
            <MainLayout>
              <StockOverview />
            </MainLayout>
          </ProtectedRoute>
        } />

        <Route path="/inventory/suppliers" element={
          <ProtectedRoute>
            <MainLayout>
              <SupplierManagement />
            </MainLayout>
          </ProtectedRoute>
        } />

        <Route path="/inventory/pricing" element={
          <ProtectedRoute>
            <MainLayout>
              <PriceLevels />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/inventory/taxes" element={
          <ProtectedRoute>
            <MainLayout>
              <TaxList />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/inventory/sales-channels" element={
          <ProtectedRoute>
            <MainLayout>
              <SalesChannelList />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/inventory/distribution-routes" element={
          <ProtectedRoute>
            <MainLayout>
              <RouteList />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/inventory/return-types" element={
          <ProtectedRoute>
            <MainLayout>
              <ReturnTypesList />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/inventory/sales-refs" element={
          <ProtectedRoute>
            <MainLayout>
              <SalesRefList />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/inventory/settings/po-approval-levels" element={
          <ProtectedRoute>
            <MainLayout>
              <POApprovalLevels />
            </MainLayout>
          </ProtectedRoute>
        } />

        <Route path="/inventory/reports" element={<Navigate to="/inventory/reports/bincard" replace />} />
        <Route path="/inventory/reports/stock-report" element={<Navigate to="/inventory/reports/stock-levels" replace />} />

        <Route path="/inventory/reports/bincard" element={
          <ProtectedRoute requiredPermission="INVENTORY:BINCARD:READ">
            <MainLayout>
              <BincardReport />
            </MainLayout>
          </ProtectedRoute>
        } />

        <Route path="/inventory/reports/stock-levels" element={
          <ProtectedRoute requiredPermission="INVENTORY:STOCK_LEVELS:READ">
            <MainLayout>
              <StockLevelsReport />
            </MainLayout>
          </ProtectedRoute>
        } />

        <Route path="/inventory/reports/stock-movements" element={
          <ProtectedRoute requiredPermission="INVENTORY:STOCK_MOVEMENTS:READ">
            <MainLayout>
              <StockMovementsReport />
            </MainLayout>
          </ProtectedRoute>
        } />

        <Route path="/inventory/reports/analytics" element={
          <ProtectedRoute requiredPermission="INVENTORY:STOCK_ANALYTICS:READ">
            <MainLayout>
              <StockAnalyticsReport />
            </MainLayout>
          </ProtectedRoute>
        } />

        <Route path="/inventory/reports/valuation" element={
          <ProtectedRoute requiredPermission="INVENTORY:STOCK_VALUATION:READ">
            <MainLayout>
              <StockValueReport />
            </MainLayout>
          </ProtectedRoute>
        } />

        <Route path="/inventory/reports/stock-operations" element={
          <ProtectedRoute requiredPermission="INVENTORY:STOCK_OPERATIONS:READ">
            <MainLayout>
              <StockOperationsReport />
            </MainLayout>
          </ProtectedRoute>
        } />

        <Route path="/inventory/reports/aging" element={
          <ProtectedRoute requiredPermission="INVENTORY:STOCK_AGING:READ">
            <MainLayout>
              <StockAgeAnalysisReport />
            </MainLayout>
          </ProtectedRoute>
        } />

        <Route path="/inventory/reports/reorder" element={
          <ProtectedRoute requiredPermission="INVENTORY:STOCK_LEVELS:READ">
            <MainLayout>
              <ReorderReport />
            </MainLayout>
          </ProtectedRoute>
        } />

        <Route path="/inventory/reports/stock-movement-summary" element={
          <ProtectedRoute requiredPermission="INVENTORY:STOCK_MOVEMENTS:READ">
            <MainLayout>
              <StockMovementSummaryReport />
            </MainLayout>
          </ProtectedRoute>
        } />

        <Route path="/inventory/reports/consumption" element={
          <ProtectedRoute requiredPermission="INVENTORY:INVENTORY_CONSUMPTION:READ">
            <MainLayout>
              <ConsumptionReport />
            </MainLayout>
          </ProtectedRoute>
        } />

        <Route path="/inventory/reports/price-list" element={
          <ProtectedRoute requiredPermission="INVENTORY:PRICING:READ">
            <MainLayout>
              <PriceListReport />
            </MainLayout>
          </ProtectedRoute>
        } />

        {/* Default redirect */}
        <Route path="/" element={<Navigate to="/dashboard" replace />} />

        {/* 404 Route */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </AppLayout>
  );
};

const NotFound = () => {
  const pathname = window.location.pathname;
  if (pathname.startsWith('/api/')) {
    const rawApiUrl = (typeof import.meta !== 'undefined' && import.meta.env?.VITE_APP_API_URL) || '';
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const apiGatewayBase = rawApiUrl.replace(/\/api\/?$/, '')
      || (isLocalhost
        ? 'http://localhost:3001'
        : window.location.hostname.includes('slim-dev')
          ? 'https://api-slim-dev.h2bis.com'
          : 'https://api-slim.h2bis.com');
    window.location.replace(`${apiGatewayBase}${pathname}${window.location.search}`);
    return null;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <h1 className="text-6xl font-bold text-gray-900">404</h1>
        <p className="text-xl text-gray-600 mt-4">Page not found</p>
        <Link to="/dashboard" className="text-indigo-600 hover:text-indigo-500 mt-4 inline-block">
          Go back to dashboard
        </Link>
      </div>
    </div>
  );
};

// Main App Component with Providers
const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <Router
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true
        }}
      >
        <AuthProvider>
          <LocationProvider>
            <ReminderProvider>
              <RBACProvider>
                <InventoryProvider>
                  <CRMProvider>
                    <AppContent />
                  </CRMProvider>
                </InventoryProvider>
              </RBACProvider>
            </ReminderProvider>
          </LocationProvider>
        </AuthProvider>
      </Router>
    </QueryClientProvider>
  );
};

export default App;
